import { useContext } from 'react';
import {
  StatusError,
  StatusOK,
  SubvalueCell,
  Table,
  TableColumn,
} from '@backstage/core-components';
import type { ClientCurrentResourceUsage } from '@backstage/plugin-kubernetes-common';
import {
  ClusterContext,
  PodDrawer,
  usePodMetrics,
  type PodAndErrors,
} from '@backstage/plugin-kubernetes-react';
import Typography from '@material-ui/core/Typography';
import {
  bytesToMiB,
  containerIssues,
  containersReady,
  formatMillicores,
  percent,
  totalRestarts,
  type Pod,
} from './lib';

const Name = ({ pod }: { pod: Pod }) => {
  const cluster = useContext(ClusterContext);
  return (
    <SubvalueCell
      value={
        <PodDrawer
          podAndErrors={{
            pod: pod as PodAndErrors['pod'],
            cluster,
            errors: [],
          }}
        />
      }
      subvalue={pod.metadata?.namespace}
    />
  );
};

const Status = ({ pod }: { pod: Pod }) => {
  const issues = containerIssues(pod);
  if (issues.length === 0) {
    return <StatusOK>OK</StatusOK>;
  }
  return (
    <>
      {issues.map(({ container, reason, ok }) => (
        <SubvalueCell
          key={`${container}-${reason}`}
          value={
            ok ? (
              <StatusOK>{container}</StatusOK>
            ) : (
              <StatusError>{container}</StatusError>
            )
          }
          subvalue={reason}
        />
      ))}
    </>
  );
};

const Usage = ({
  usage,
  format,
}: {
  usage?: ClientCurrentResourceUsage;
  format: (value: number | string) => string;
}) => {
  if (!usage) {
    return <Typography color="textSecondary">unknown</Typography>;
  }
  const hasRequest = parseFloat(String(usage.requestTotal)) > 0;
  const hasLimit = parseFloat(String(usage.limitTotal)) > 0;
  return (
    <SubvalueCell
      value={
        hasRequest
          ? `${format(usage.currentUsage)} · ${percent(usage.currentUsage, usage.requestTotal)}% of ${format(usage.requestTotal)} requested`
          : `${format(usage.currentUsage)} · no request`
      }
      subvalue={hasLimit ? `limit ${format(usage.limitTotal)}` : 'no limit'}
    />
  );
};

const useMetrics = (pod: Pod) => {
  const cluster = useContext(ClusterContext);
  return usePodMetrics(cluster.name, {
    metadata: { name: pod.metadata?.name, namespace: pod.metadata?.namespace },
  });
};

const Cpu = ({ pod }: { pod: Pod }) => (
  <Usage usage={useMetrics(pod)?.cpu} format={formatMillicores} />
);

const Memory = ({ pod }: { pod: Pod }) => (
  <Usage usage={useMetrics(pod)?.memory} format={bytesToMiB} />
);

const columns: TableColumn<Pod>[] = [
  {
    title: 'Name',
    width: '28%',
    highlight: true,
    render: pod => <Name pod={pod} />,
  },
  {
    title: 'Phase',
    width: '8%',
    render: pod => pod.status?.phase ?? 'Unknown',
  },
  { title: 'Status', width: '20%', render: pod => <Status pod={pod} /> },
  {
    title: 'Containers ready',
    width: '9%',
    align: 'center',
    render: containersReady,
  },
  {
    title: 'Restarts',
    width: '8%',
    align: 'center',
    render: totalRestarts,
  },
  { title: 'CPU usage', width: '13.5%', render: pod => <Cpu pod={pod} /> },
  {
    title: 'Memory usage',
    width: '13.5%',
    render: pod => <Memory pod={pod} />,
  },
];

export const PodTable = ({ pods }: { pods: Pod[] }) => (
  <Table
    options={{
      paging: true,
      search: false,
      emptyRowsWhenPaging: false,
      tableLayout: 'fixed',
    }}
    data={pods.map(pod => ({ ...pod, id: pod.metadata?.uid }))}
    columns={columns}
  />
);
